import { Box, TextField } from '@mui/material';
import { LabeledValue } from '~/components/Fields/LabeledValue';
import NumberInput from '~/components/Inputs/NumberInput';
import { DicomVRMaxLength } from '~/domain/dicom/dicomConstants';
import {
    INTEGER_STRING_MAX,
    dicomStringViolation,
    isConformantIntegerString,
} from '~/domain/dicom/valueRepresentation';
import { Image3D } from '~/domain/patient/image3d';
import { ImageSeriesEdits } from '~/hooks/export/useExportEditing';

type ImageExportDetailsProps = {
    image: Image3D;
    edits?: ImageSeriesEdits;
    onSeriesDescriptionChange: (description: string) => void;
    onSeriesNumberChange: (seriesNumber: number) => void;
};

export default function ImageExportDetails({
    image,
    edits,
    onSeriesDescriptionChange,
    onSeriesNumberChange,
}: ImageExportDetailsProps) {
    const isEditable = image.isNew;
    const seriesDescription =
        edits?.seriesDescription ?? image.seriesMetadata.seriesDescription;
    const seriesNumber =
        edits?.seriesNumber ?? image.seriesMetadata.seriesNumber;

    // The same rules the reformat writes under: these values reach an
    // exported object, where a receiver reads them under the same VRs.
    // SeriesDescription is Type 3, so empty is legal.
    const descriptionViolation = dicomStringViolation(
        seriesDescription ?? '',
        DicomVRMaxLength.LONG,
        { allowEmpty: true }
    );

    return (
        <Box
            py={1}
            display="grid"
            gridTemplateColumns="minmax(0,2fr) minmax(0,1fr) minmax(0,1fr)"
            gap={1}
        >
            {isEditable ? (
                <TextField
                    label="Series Description"
                    value={seriesDescription}
                    onChange={(e) => onSeriesDescriptionChange(e.target.value)}
                    size="small"
                    variant="outlined"
                    inputProps={{ maxLength: DicomVRMaxLength.LONG }}
                    error={descriptionViolation !== null}
                    helperText={
                        descriptionViolation === 'backslash'
                            ? 'Cannot contain a backslash'
                            : descriptionViolation === 'characters'
                              ? 'ASCII characters only'
                              : undefined
                    }
                />
            ) : (
                <LabeledValue
                    label="Series Description"
                    value={seriesDescription}
                    sensitive
                />
            )}
            {isEditable ? (
                <NumberInput
                    label="Series Number"
                    value={seriesNumber ?? null}
                    onChange={(typed) => {
                        // Only a value that can be written as an Integer
                        // String is committed; an entry with a fractional
                        // part is shown as invalid, never read as its
                        // integer part. It is committed on Enter or on
                        // leaving the field, never on its way to being
                        // typed: the number is written to the exported
                        // series.
                        if (
                            typed !== null &&
                            isConformantIntegerString(typed)
                        ) {
                            onSeriesNumberChange(typed);
                        }
                    }}
                    min={0}
                    max={INTEGER_STRING_MAX}
                    step={1}
                    precision={0}
                    fullWidth
                />
            ) : (
                <LabeledValue label="Series Number" value={seriesNumber} />
            )}
            <LabeledValue label="Slices" value={image.length} />
            <LabeledValue
                label="Generation Model"
                value={image.generationModel?.label}
            />
            {image.firstSlice.manufacturerModelName && (
                <LabeledValue
                    label="Manufacturer Model Name"
                    value={image.firstSlice.manufacturerModelName}
                />
            )}
        </Box>
    );
}
